import { ThemeOptions, outlinedInputClasses } from "@mui/material";
import { Fade } from "@mui/material";

import Rubik from "./Rubik";
import Realtime from "./Realtime";

export const radius: Required<ThemeOptions>["radius"] = {
  smaller: "2px",
  small: "4px",
  medium: "8px",
};
const colorSelectorsForOutlinedInput = (color: string) => ({
  "& > .MuiOutlinedInput-notchedOutline": {
    borderColor: color,
  },
  "& .MuiInputAdornment-root > .MuiSvgIcon-root": {
    color: color,
  },
});
export const components: ThemeOptions["components"] = {
  MuiTooltip: {
    defaultProps: {
      TransitionComponent: Fade,
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& > .MuiToggleButton-root": {
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: theme.palette.common.grey.light,
          backgroundColor: "transparent",
          padding: theme.spacing(0.75, 2),
          borderRadius: radius.medium,
          "&:not(:last-of-type)": {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
          "&:not(:first-of-type)": {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            "&.MuiToggleButtonGroup-grouped": {
              borderLeftColor: theme.palette.common.grey.light,
            },
          },
          "&:hover": {
            backgroundColor: theme.palette.common.grey.g474a53,
          },
          "&.Mui-disabled": {
            borderColor: theme.palette.background.paper,
          },
          "&.Mui-selected": {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.common.black,
            "&.Mui-disabled": {
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.common.grey.g9B9B9B,
            },
          },
        },
      }),
    },
  },
  MuiToggleButton: {
    variants: [
      {
        props: { size: "small" },
        style: { height: "2.3em" },
      },
      {
        props: { size: "medium" },
        style: { height: "2.5rem" },
      },
      {
        props: { size: "large" },
        style: { height: "3rem" },
      },
    ],
  },
  MuiSelect: {
    defaultProps: {
      size: "small",
    },
  },
  MuiOutlinedInput: {
    variants: [
      {
        props: { color: "error" },
        style: ({ theme }) => {
          const selectors = colorSelectorsForOutlinedInput(
            theme.palette.error.main
          );
          return {
            "&.Mui-error": selectors,
            ...selectors,
          };
        },
      },
      {
        props: { color: "warning" },
        style: ({ theme }) => {
          const selectors = colorSelectorsForOutlinedInput(
            theme.palette.warning.main
          );
          return {
            "&.Mui-error": selectors,
            ...selectors,
          };
        },
      },
      {
        props: { color: "success" },
        style: ({ theme }) =>
          colorSelectorsForOutlinedInput(theme.palette.success.main),
      },
    ],
  },
  MuiFormControl: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        "& .MuiFilledInput-root": {
          backgroundColor: theme.palette.background.paper,
          borderTopLeftRadius: radius.small,
          borderTopRightRadius: radius.small,
        },
        "& .MuiOutlinedInput-root": {
          "&.Mui-disabled": {
            backgroundColor: theme.palette.background.paper,
            "& > fieldset.MuiOutlinedInput-notchedOutline": {
              borderColor: "transparent",
              backgroundColor: "transparent",
            },
            "&.MuiInputLabel-root": {
              backgroundColor: "transparent",
            },
          },
        },
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: ({ theme }) => ({
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: "fit-content",
      }),
    },
  },
  MuiTextField: {
    defaultProps: {
      size: "small",
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.palette.error.dark,
      }),
    },
  },
  MuiCssBaseline: {
    styleOverrides: `
    ${Rubik} ${Realtime},
    *::-webkit-scrollbar {
      width: 0.3rem;
      height: 0.5rem;
    },
    *::-webkit-scrollbar-track {
      background: transparent;
    },
    *::-webkit-scrollbar-thumb {
      background-color: #707070;
      border-radius: 1rem;
    },
    ::-webkit-scrollbar-corner {
      background-color: transparent;
    },
  `,
  },
  MuiPaginationItem: {
    variants: [
      {
        props: { shape: "rounded" },
        style: { borderRadius: radius.smaller },
      },
    ],
    styleOverrides: {
      root: ({ theme }) => ({
        "&.Mui-selected": {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.background.dark,
        },
      }),
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        height: 26,
        width: 56,
        padding: 0,
      },
      switchBase: ({ theme }) => ({
        padding: 0,
        margin: 2,
        "&.Mui-checked": {
          transform: "translateX(30px)",
          color: theme.palette.background.default,
          "&.Mui-disabled": {
            color: theme.palette.background.default,
          },
        },
        "&.Mui-checked+.MuiSwitch-track": {
          opacity: 1,
        },
        "&.Mui-disabled+.MuiSwitch-track": {
          backgroundColor: theme.palette.common.grey.dark,
        },
      }),
      track: ({ theme }) => ({
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.grey[800],
        opacity: 1,
        transition: theme.transitions.create(["background-color"], {
          duration: 500,
        }),
      }),
      thumb: {
        boxSizing: "border-box",
        width: 22,
        height: 22,
      },
    },
  },
};
